import { supabase } from "@/integrations/supabase/client";
import { db, type InvoiceRecord, type PackageRecord, type SubscriptionRecord } from "@/lib/billing";
import { normalizeLanguage, type SupportedLanguage } from "@/i18n";

export type ProfileForm = {
  full_name: string;
  phone_number: string;
  company_name: string;
  address: string;
  country: string;
  preferred_language: SupportedLanguage;
  avatar_url: string;
};

export type UserProfileRecord = ProfileForm & {
  id: string;
  email: string | null;
  created_at: string | null;
  account_status: string;
  email_verified: boolean;
  last_login_at: string | null;
  subscription: (SubscriptionRecord & { packages?: PackageRecord | null }) | null;
  latest_invoice: InvoiceRecord | null;
  payment_status: string | null;
};

export const blankProfileForm: ProfileForm = {
  full_name: "",
  phone_number: "",
  company_name: "",
  address: "",
  country: "",
  preferred_language: "fr",
  avatar_url: "",
};

export function sanitizeProfileForm(form: ProfileForm) {
  return {
    full_name: form.full_name.trim(),
    phone_number: form.phone_number.trim() || null,
    company_name: form.company_name.trim() || null,
    address: form.address.trim() || null,
    country: form.country.trim() || null,
    preferred_language: normalizeLanguage(form.preferred_language) ?? "fr",
    language_preference: normalizeLanguage(form.preferred_language) ?? "fr",
    avatar_url: form.avatar_url.trim() || null,
    last_profile_update_at: new Date().toISOString(),
  };
}

export function validateProfileForm(form: ProfileForm) {
  const errors: Partial<Record<keyof ProfileForm, string>> = {};
  if (!form.full_name.trim()) errors.full_name = "Le nom complet est obligatoire.";
  if (form.phone_number.trim() && !/^[+()\d\s.-]{6,32}$/.test(form.phone_number.trim())) {
    errors.phone_number = "Le numero de telephone n'est pas valide.";
  }
  if (!normalizeLanguage(form.preferred_language)) errors.preferred_language = "Langue non prise en charge.";
  return errors;
}

export function profileToForm(profile: Partial<UserProfileRecord> | null | undefined): ProfileForm {
  return {
    full_name: profile?.full_name ?? "",
    phone_number: profile?.phone_number ?? "",
    company_name: profile?.company_name ?? "",
    address: profile?.address ?? "",
    country: profile?.country ?? "",
    preferred_language: normalizeLanguage(profile?.preferred_language) ?? "fr",
    avatar_url: profile?.avatar_url ?? "",
  };
}

export async function fetchUserProfile(userId: string, email?: string | null): Promise<UserProfileRecord> {
  const [profileResult, statusResult, subscriptionResult, invoiceResult] = await Promise.all([
    db.from("profiles").select("*").eq("id", userId).maybeSingle(),
    db.from("user_account_status").select("*").eq("user_id", userId).maybeSingle(),
    db.from("user_subscriptions").select("*, packages(*)").eq("user_id", userId).order("created_at", { ascending: false }).limit(1).maybeSingle(),
    db.from("invoices").select("*, packages(name)").eq("user_id", userId).order("created_at", { ascending: false }).limit(1).maybeSingle(),
  ]);

  if (profileResult.error) throw profileResult.error;
  const profile = profileResult.data;
  const status = statusResult.error ? null : statusResult.data;
  const subscription = subscriptionResult.error ? null : subscriptionResult.data;
  const latestInvoice = invoiceResult.error ? null : invoiceResult.data;

  return {
    ...blankProfileForm,
    id: userId,
    email: email ?? null,
    full_name: profile?.full_name ?? "",
    phone_number: profile?.phone_number ?? status?.phone_number ?? "",
    company_name: profile?.company_name ?? "",
    address: profile?.address ?? "",
    country: profile?.country ?? "",
    preferred_language: normalizeLanguage(profile?.preferred_language ?? profile?.language_preference) ?? "fr",
    avatar_url: profile?.avatar_url ?? "",
    created_at: profile?.created_at ?? null,
    account_status: status?.status ?? profile?.account_status ?? "unknown",
    email_verified: Boolean(status?.email_verified),
    last_login_at: status?.last_login_at ?? null,
    subscription,
    latest_invoice: latestInvoice,
    payment_status: latestInvoice?.status ?? subscription?.status ?? null,
  };
}

export async function saveOwnProfile(userId: string, form: ProfileForm) {
  const patch = sanitizeProfileForm(form);
  const { error } = await db.from("profiles").upsert({ id: userId, ...patch }, { onConflict: "id" });
  if (error) throw error;
  await supabase.auth.updateUser({
    data: {
      full_name: patch.full_name,
      phone_number: patch.phone_number,
      avatar_url: patch.avatar_url,
      company_name: patch.company_name,
      preferred_language: patch.preferred_language,
    },
  });
  return patch;
}
